import { BaseAdapter } from "./base-adapter";
import {
  type TEvent,
  EventType,
  type IPageViewEvent,
  type IFormSubmissionEvent,
  type IViewItemEvent,
  type IViewedProductEvent,
  type IAddToCartEvent,
  type ICheckoutStartedEvent,
  type ICheckoutCompletedEvent,
  type ISearchEvent,
  type ICustomEvent,
  type IAddPaymentInfoEvent,
  type ICheckoutPaymentEvent,
  type IViewSearchResultsEvent,
  type IOrderPlacedEvent,
  type IBeginCheckoutEvent,
  type IViewContentEvent,
  type IPurchaseEvent,
} from "../types";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Configuration for the Google Analytics adapter
 */
export interface GoogleAdapterConfig {
  /**
   * Google Analytics measurement ID (e.g., G-XXXXXXXXXX)
   */
  measurementId: string;
}

/**
 * Google Analytics tracking adapter
 * Assumes that the Google Analytics script is already loaded in the page
 */
export class GoogleAdapter extends BaseAdapter {
  public readonly name = "GoogleAnalytics";

  constructor(config: GoogleAdapterConfig) {
    super(config);
    // Initialize logger with proper adapter name
    this.initializeLogger();
  }

  /**
   * Initialize Google Analytics adapter
   * This just checks if gtag is available
   */
  public isEnabled(): boolean {
    const enabled =
      this.initialized && typeof window !== "undefined" && !!window.gtag;

    this.logger.debug("Checking if enabled", {
      initialized: this.initialized,
      hasWindow: typeof window !== "undefined",
      hasGtag: !!window?.gtag,
      enabled,
    });

    return enabled;
  }

  public async initialize(): Promise<void> {
    this.logger.info("Starting initialization");

    if (this.initialized) {
      this.logger.debug("Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      this.logger.debug("Skipping initialization on server");
      return;
    }

    const measurementId = this.getConfig<string>("measurementId");
    this.logger.info("Using measurement ID", { measurementId });

    // Check if gtag is available
    if (!window.gtag) {
      this.logger.warn(
        "Google Analytics gtag not found. Make sure the script is loaded."
      );
      return;
    }

    this.initialized = true;
    this.logger.info("Successfully initialized", { measurementId });
  }

  /**
   * Track an event with Google Analytics
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    this.logger.debug("Attempting to track event", {
      initialized: this.initialized,
      hasWindow: typeof window !== "undefined",
      hasGtag: !!window?.gtag,
      eventType: event.type,
    });

    if (!this.initialized || typeof window === "undefined" || !window.gtag) {
      this.logger.warn(
        "Cannot track event, adapter not initialized or gtag not available",
        {
          eventType: event.type,
          initialized: this.initialized,
          hasWindow: typeof window !== "undefined",
          hasGtag: !!window?.gtag,
        }
      );
      return;
    }

    const measurementId = this.getConfig<string>("measurementId");
    this.logger.info("Tracking event", {
      eventType: event.type,
      measurementId,
    });

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event as IPageViewEvent, measurementId);
        break;
      case EventType.USER_ENGAGEMENT:
        this.trackUserEngagement();
        break;
      case EventType.SESSION_START:
        this.trackSessionStart();
        break;
      case EventType.FIRST_VISIT:
        this.trackFirstVisit();
        break;
      case EventType.VIEW_ITEM:
        this.trackViewItem(event as IViewItemEvent);
        break;
      case EventType.VIEWED_PRODUCT:
        this.trackViewedProduct(event as IViewedProductEvent);
        break;
      case EventType.CHECKOUT_PAYMENT:
        this.trackCheckoutPayment(event as ICheckoutPaymentEvent);
        break;
      case EventType.SCROLL:
        this.trackScroll();
        break;
      case EventType.ADD_TO_CART:
      case EventType.SHOPIFY_ADD_TO_CART:
        this.trackAddToCart(event as IAddToCartEvent);
        break;
      case EventType.FORM_START:
        this.trackFormStart();
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event as ICheckoutStartedEvent);
        break;
      case EventType.FORM_SUBMISSION:
        this.trackFormSubmission(event as IFormSubmissionEvent);
        break;
      case EventType.SEARCH:
        this.trackSearch(event as ISearchEvent);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event as ICheckoutCompletedEvent);
        break;
      case EventType.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(event as IAddPaymentInfoEvent);
        break;
      case EventType.VIEW_CONTENT:
        this.trackViewContent(event as IViewContentEvent);
        break;
      case EventType.PURCHASE:
        this.trackPurchase(event as IPurchaseEvent);
        break;
      case EventType.VIEW_SEARCH_RESULTS:
        this.trackViewSearchResults(event as IViewSearchResultsEvent);
        break;
      case EventType.ORDER_PLACED:
        this.trackOrderPlaced(event as IOrderPlacedEvent);
        break;
      case EventType.BEGIN_CHECKOUT:
        this.trackBeginCheckout(event as IBeginCheckoutEvent);
        break;
      case EventType.CUSTOM:
        this.trackCustomEvent(event as ICustomEvent);
        break;

      default: {
        // For other events, track as custom events
        const params = this.eventToParams(event);
        window.gtag(
          "event",
          event.type,
          this.enhanceWithAffiliateParams(params)
        );
        break;
      }
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   * @param measurementId The Google Analytics measurement ID
   */
  private trackPageView(event: IPageViewEvent, measurementId: string): void {
    const params = {
      page_path: event.path,
      page_title: event.title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer || undefined,
      ga_session_id: event.ga_session_id || "",
      ga_session_number: event.ga_session_number || "",
      gclid: event.gclid || "",
      engagement_time_msec: event.engagement_time_msec,
      campaign: event.campaign || "",
      campaign_id: event.campaign_id || "",
      content: event.content || "",
      debug_mode: event.debug_mode,
      medium: event.medium || "",
      source: event.page_source || "",
      page_term: event.page_term || "",
      ignore_referrer: event.ignore_referrer,
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      user_id: event.user_id,
      user_properties: event.user_properties,
      session_engaged: event.session_engaged,
    };

    window.gtag?.(
      "event",
      "page_view",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackUserEngagement(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: "",
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      source: "",
      srsltid: "",
      term: "",
    };

    window.gtag?.(
      "event",
      "user_engagement",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackSessionStart(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      campaign_id: "",
      content: "",
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: "",
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      source: "",
      srsltid: "",
      term: "",
    };

    window.gtag?.(
      "event",
      "session_start",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackFirstVisit(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      campaign_id: "",
      content: "",
      debug_mode: "",
      engagement_time_msec: 0,
      entrances: 1,
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: "",
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      page_source: "",
      page_term: "",
    };

    window.gtag?.(
      "event",
      "first_visit",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackViewItem(event: IViewItemEvent): void {
    window.gtag?.(
      "event",
      "view_item",
      this.enhanceWithAffiliateParams({
        currency: event.currency,
        value: event.value,
        items: event.items,
      })
    );
  }

  private trackScroll(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      percent_scrolled: 90, // or dynamically captured
    };

    window.gtag?.("event", "scroll", this.enhanceWithAffiliateParams(params));
  }

  private trackCheckoutPayment(event: ICheckoutPaymentEvent): void {
    window.gtag?.(
      "event",
      "checkout_payment",
      this.enhanceWithAffiliateParams({
        currency: event.currency,
        value: event.cartValue,
        items: event.items.map((item) => ({
          item_id: item.productId,
          item_name: item.productName,
          price: item.price,
          quantity: item.quantity,
        })),
      })
    );
  }

  private trackViewedProduct(event: IViewedProductEvent): void {
    window.gtag?.(
      "event",
      "viewed_product",
      this.enhanceWithAffiliateParams({
        currency: event.currency,
        value: event.price,
        items: [
          {
            item_id: event.productId,
            item_name: event.productName,
            price: event.price,
            currency: event.currency,
          },
        ],
        view_duration: event.viewDuration,
      })
    );
  }

  private trackAddToCart(event: IAddToCartEvent): void {
    this.logger.debug("Processing add_to_cart event", { event });

    const params = {
      currency: event.currency || "INR",
      value: event.price * (event.quantity || 1), // Calculate total value
      items: [
        {
          item_id: event.productId,
          item_name: event.productName,
          price: event.price,
          quantity: event.quantity || 1,
          item_variant: event.variant,
        },
      ],
      // GA4-specific parameters
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      campaign: event.campaign || "",
      ga_session_id: event.ga_session_id || "",
      engagement_time_msec: event.engagement_time_msec,
      session_engaged: event.session_engaged,
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer,
      user_id: event.user_id,
      user_properties: event.user_properties,
    };

    const enhancedParams = this.enhanceWithAffiliateParams(params);
    this.logger.debug("Sending add_to_cart event to gtag", {
      params: enhancedParams,
    });

    window.gtag?.("event", "add_to_cart", enhancedParams);
  }

  private trackFormStart(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      first_field_id: "",
      first_field_name: "",
      first_field_position: "",
      first_field_type: "",
      form_destination: "",
      form_length: "",
      form_id: "",
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
    };

    window.gtag?.(
      "event",
      "form_start",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      currency: event.currency || "INR",
      ecomm_pagetype: "",
      ecomm_prodid: "",
      ecomm_totalvalue: event.cartValue,
      value: event.cartValue,
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
      items: event.items.map((item) => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        item_variant: item.variant,
      })),
    };

    window.gtag?.(
      "event",
      "begin_checkout",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackFormSubmission(event: IFormSubmissionEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      form_destination: "",
      form_length: "",
      form_id: event.formId,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
    };

    window.gtag?.(
      "event",
      "form_submit",
      this.enhanceWithAffiliateParams(params)
    );
  }

  private trackSearch(event: ISearchEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
      search_term: event.searchTerm,
    };

    window.gtag?.("event", "search", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    const params = {
      transaction_id: event.orderId,
      currency: event.currency || "INR",
      value: event.cartValue,
      items: event.items.map((item) => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        item_variant: item.variant,
      })),
    };

    window.gtag?.("event", "purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a custom event
   * @param event The custom event
   */
  private trackCustomEvent(event: ICustomEvent): void {
    const params = event.properties || {};
    window.gtag?.("event", event.name, this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track an add payment info event
   * @param event The add payment info event
   */
  private trackAddPaymentInfo(event: IAddPaymentInfoEvent): void {
    const params = {
      currency: event.currency || "INR",
      value: event.cartValue,
      payment_type: event.paymentType || "",
      items: event.items.map((item) => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        item_variant: item.variant,
      })),
    };

    window.gtag?.(
      "event",
      "add_payment_info",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a view content event
   * @param event The view content event
   */
  private trackViewContent(event: IViewContentEvent): void {
    const params = {
      currency: event.currency || "USD",
      value: event.value || 0,
      items:
        event.items?.map((item: any) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_category: item.item_category,
          item_category2: item.item_category2,
          item_category3: item.item_category3,
          item_category4: item.item_category4,
          item_category5: item.item_category5,
          item_brand: item.item_brand,
          item_variant: item.item_variant,
          price: item.price,
          quantity: item.quantity,
        })) || [],
      // GA4-specific parameters
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      campaign: event.campaign || "",
      ga_session_id: event.ga_session_id || "",
      engagement_time_msec: event.engagement_time_msec,
      session_engaged: event.session_engaged,
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer,
      user_id: event.user_id,
      user_properties: event.user_properties,
    };

    window.gtag?.(
      "event",
      "view_item",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track a purchase event
   * @param event The purchase event
   */
  private trackPurchase(event: IPurchaseEvent): void {
    const params = {
      transaction_id: event.transaction_id,
      value: event.value,
      currency: event.currency || "USD",
      coupon: event.coupon,
      shipping: event.shipping,
      tax: event.tax,
      items:
        event.items?.map((item: any) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_category: item.item_category,
          item_category2: item.item_category2,
          item_category3: item.item_category3,
          item_category4: item.item_category4,
          item_category5: item.item_category5,
          item_brand: item.item_brand,
          item_variant: item.item_variant,
          price: item.price,
          quantity: item.quantity,
        })) || [],
      // GA4-specific parameters
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      campaign: event.campaign || "",
      ga_session_id: event.ga_session_id || "",
      engagement_time_msec: event.engagement_time_msec,
      session_engaged: event.session_engaged,
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer,
      user_id: event.user_id,
      user_properties: event.user_properties,
    };

    window.gtag?.("event", "purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a view search results event
   * @param event The view search results event
   */
  private trackViewSearchResults(event: IViewSearchResultsEvent): void {
    const params = {
      search_term: event.search_term,
      // GA4-specific parameters
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      campaign: event.campaign || "",
      ga_session_id: event.ga_session_id || "",
      engagement_time_msec: event.engagement_time_msec,
      session_engaged: event.session_engaged,
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer,
      user_id: event.user_id,
      user_properties: event.user_properties,
    };

    window.gtag?.(
      "event",
      "view_search_results",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Track an order placed event
   * @param event The order placed event
   */
  private trackOrderPlaced(event: IOrderPlacedEvent): void {
    const params = {
      transaction_id: event.order_id,
      value: event.total_amount,
      currency: event.currency || "USD",
      items:
        event.items?.map((item: any) => ({
          item_id: item.product_id,
          item_name: item.product_name,
          item_category: item.category,
          item_brand: item.brand,
          item_variant: item.variant,
          price: item.price,
          quantity: item.quantity,
        })) || [],
      // GA4-specific parameters
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      campaign: event.campaign || "",
      ga_session_id: event.ga_session_id || "",
      engagement_time_msec: event.engagement_time_msec,
      session_engaged: event.session_engaged,
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer,
      user_id: event.user_id,
      user_properties: event.user_properties,
    };

    window.gtag?.("event", "purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a begin checkout event
   * @param event The begin checkout event
   */
  private trackBeginCheckout(event: IBeginCheckoutEvent): void {
    const params = {
      currency: event.currency || "USD",
      value: event.value || 0,
      coupon: event.coupon,
      items:
        event.items?.map((item: any) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_category: item.item_category,
          item_category2: item.item_category2,
          item_category3: item.item_category3,
          item_category4: item.item_category4,
          item_category5: item.item_category5,
          item_brand: item.item_brand,
          item_variant: item.item_variant,
          price: item.price,
          quantity: item.quantity,
        })) || [],
      // GA4-specific parameters
      batch_ordering_id: event.batch_ordering_id || "",
      batch_page_id: event.batch_page_id || "",
      campaign: event.campaign || "",
      ga_session_id: event.ga_session_id || "",
      engagement_time_msec: event.engagement_time_msec,
      session_engaged: event.session_engaged,
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_referrer: event.page_referrer || document.referrer,
      user_id: event.user_id,
      user_properties: event.user_properties,
    };

    window.gtag?.(
      "event",
      "begin_checkout",
      this.enhanceWithAffiliateParams(params)
    );
  }

  /**
   * Convert an event to Google Analytics parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return this.enhanceWithAffiliateParams(params);
  }
}
